import React, { useState, useMemo, useRef } from 'react';
import { getEmptyPromptCollection } from '../types/product';
import { storage, auth } from '../firebase';
import { ref as fbRef, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * BulkImportModal — Smart Custom only.
 * Two modes:
 *   - Bulk: 1 row Excel = 1 product (Name, Image URL, prompts in columns).
 *   - Single: 1 product (manual entry + image upload), 3 rows Excel = Set 1/2/3 prompts.
 *
 * Steps depending on mode:
 *   Bulk:   Scene → Paste → Mapping → Preview
 *   Single: Scene → Setup → Paste → Mapping → Preview
 */

// Field mapping target options — bergantung pada scene count + mode
// Bulk mode: ada Product Info (Name, Image URL) + Set 1/2/3 fields (prefix 'setX.')
// Single mode: tiada Product Info (sebab manual setup form), tiada set prefix (row index = set)
// Strukur: { value, label, group, required }
const fieldOptionsForScene = (sceneCount, mode = 'bulk') => {
    if (mode === 'single') {
        // Single mode: rows = sets, mapping pilih FIELD dalam set sahaja
        // Product Number — single value (taken from row 1)
        // Note: Internal value names guna storage canonical names (`promptVideo1`/`promptVideo2`)
        // — yang sama dengan UI product card reads. Label "Video Scene 1/2" untuk readability.
        const opts = [
            { value: 'skip', label: '— Skip —', group: null },
            { value: 'productNumber', label: 'Product Number (from Row 1)', group: 'Product Info', required: false },
            { value: 'fullPrompt', label: 'Full Prompt (auto-split [marker]) *', group: 'Set Fields (Required)', required: true, isFullPrompt: true },
            { value: 'promptImage', label: 'Image Prompt *', group: 'Set Fields (Required)', required: true },
        ];
        if (sceneCount === 2) {
            opts.push(
                { value: 'promptVideo1', label: 'Video Scene 1 *', group: 'Set Fields (Required)', required: true },
                { value: 'promptVideo2', label: 'Video Scene 2 *', group: 'Set Fields (Required)', required: true },
            );
        } else {
            opts.push({ value: 'promptVideo', label: 'Video Prompt *', group: 'Set Fields (Required)', required: true });
        }
        opts.push({ value: 'captionTiktok', label: 'Caption TikTok', group: 'Set Fields (Optional)', required: false });
        return opts;
    }
    // Bulk mode (existing behavior)
    const productOpts = [
        { value: 'productNumber', label: 'Product Number', group: 'Product Info', required: false },
        { value: 'name', label: 'Product Name *', group: 'Product Info', required: true },
        { value: 'imageUrl', label: 'Image URL *', group: 'Product Info', required: true },
    ];
    const setFields = (setNum) => {
        const isReq = setNum === 1; // Hanya Set 1 required
        const reqMark = isReq ? ' *' : '';
        const groupName = `Set ${setNum}${isReq ? ' (Required)' : ' (Optional)'}`;
        // Prefix label dengan "Set X —" supaya selected state nampak jelas (HTML select tak tunjuk optgroup label bila tutup)
        const setPrefix = `Set ${setNum} — `;
        // Full Prompt option: split satu cell jadi 4 field guna markers [IMAGE]/[SCENE1]/[SCENE2]/[CAPTION]
        const fullPromptOpt = {
            value: `set${setNum}.fullPrompt`,
            label: `${setPrefix}Full Prompt (auto-split [marker])${reqMark}`,
            group: groupName,
            required: isReq,
            set: setNum,
            isFullPrompt: true,
        };
        if (sceneCount === 2) {
            return [
                fullPromptOpt,
                { value: `set${setNum}.promptImage`, label: `${setPrefix}Image Prompt${reqMark}`, group: groupName, required: isReq, set: setNum },
                { value: `set${setNum}.promptVideoScene1`, label: `${setPrefix}Video Scene 1${reqMark}`, group: groupName, required: isReq, set: setNum },
                { value: `set${setNum}.promptVideoScene2`, label: `${setPrefix}Video Scene 2${reqMark}`, group: groupName, required: isReq, set: setNum },
                { value: `set${setNum}.captionTiktok`, label: `${setPrefix}Caption TikTok`, group: groupName, required: false, set: setNum },
            ];
        }
        return [
            fullPromptOpt,
            { value: `set${setNum}.promptImage`, label: `${setPrefix}Image Prompt${reqMark}`, group: groupName, required: isReq, set: setNum },
            { value: `set${setNum}.promptVideo`, label: `${setPrefix}Video Prompt${reqMark}`, group: groupName, required: isReq, set: setNum },
            { value: `set${setNum}.captionTiktok`, label: `${setPrefix}Caption TikTok`, group: groupName, required: false, set: setNum },
        ];
    };
    return [
        { value: 'skip', label: '— Skip —', group: null },
        ...productOpts,
        ...setFields(1),
        ...setFields(2),
        ...setFields(3),
    ];
};

// Required fields — bergantung pada mode
// Bulk: Product Name, Image URL, dan SEMUA Set 1 fields. Set 2/3 fully optional.
// Single: hanya per-row set fields. Name/Image dari form.
// Note: Field names (`promptVideo1`/`promptVideo2`) match storage canonical (UI display reads same names).
const requiredFieldsForScene = (sceneCount, mode = 'bulk') => {
    if (mode === 'single') {
        return sceneCount === 2
            ? ['promptImage', 'promptVideo1', 'promptVideo2']
            : ['promptImage', 'promptVideo'];
    }
    return sceneCount === 2
        ? ['name', 'imageUrl', 'set1.promptImage', 'set1.promptVideo1', 'set1.promptVideo2']
        : ['name', 'imageUrl', 'set1.promptImage', 'set1.promptVideo'];
};

// Fuzzy keyword match — auto-detect mapping
// Field-level keywords (apply for any set when set prefix detected)
// Note: Field names = storage canonical (`promptVideo1`/`promptVideo2` not `promptVideoScene1`/`Scene2`)
const FIELD_TYPE_KEYWORDS = {
    promptImage: ['image prompt', 'img prompt', 'prompt image'],
    promptVideo: ['video prompt', 'prompt video'], // 1-scene
    promptVideo1: ['scene 1', 'video scene 1', 'video 1', 'prompt scene 1', 'scene_1'],
    promptVideo2: ['scene 2', 'video scene 2', 'video 2', 'prompt scene 2', 'scene_2'],
    captionTiktok: ['caption', 'tiktok caption', 'caption tiktok', 'tt caption', 'tiktok'],
};
// Product-level keywords (no set prefix)
const PRODUCT_KEYWORDS = {
    productNumber: ['no.', 'no', 'number', 'index', '#', 'num', 'bil', 'bilangan'],
    name: ['product name', 'name', 'title', 'product'],
    imageUrl: ['image url', 'img url', 'image link', 'img link', 'image', 'img', 'photo'],
};
// Set prefix detection — "set 1", "s1", "set1", etc.
function detectSetPrefix(header) {
    const m = header.match(/(?:^|\s|_|-)(?:set\s*|s)([1-3])(?:[\s_:-]|$)/);
    return m ? Number(m[1]) : null;
}

// ─── Full Prompt parsing — split satu cell guna markers [IMAGE] [SCENE1] [SCENE2] [VIDEO] [CAPTION] ───
// Order-independent. Returns { IMAGE, SCENE1, SCENE2, VIDEO, CAPTION } — missing keys = undefined.
// Special return { __noMarkers: true } kalau cell tiada any marker.
export function parseFullPrompt(text) {
    if (!text || typeof text !== 'string') return { __noMarkers: true };
    const markerRegex = /\[(IMAGE|SCENE1|SCENE2|VIDEO|CAPTION)\]/gi;
    const matches = [...text.matchAll(markerRegex)];
    if (matches.length === 0) return { __noMarkers: true };
    const sections = {};
    for (let i = 0; i < matches.length; i++) {
        const marker = matches[i][1].toUpperCase();
        const start = matches[i].index + matches[i][0].length;
        const end = i + 1 < matches.length ? matches[i + 1].index : text.length;
        sections[marker] = text.slice(start, end).trim();
    }
    return sections;
}

// Validate rows untuk fullPrompt mappings — return list of errors per row
// Bulk mode: fullPrompt mapping value = "setX.fullPrompt"; rowIdx = product, setNum from value
// Single mode: fullPrompt mapping value = "fullPrompt"; rowIdx = set (0=Set1, 1=Set2, 2=Set3)
// Errors return shape: [{ rowIdx, colIdx, setNum, msg }]
function validateFullPromptRows(rows, mapping, sceneCount, mode = 'bulk') {
    const errors = [];
    const requiredMarkers = sceneCount === 2 ? ['IMAGE', 'SCENE1', 'SCENE2'] : ['IMAGE', 'VIDEO'];

    if (mode === 'single') {
        const fullPromptCols = Object.entries(mapping)
            .filter(([, v]) => v === 'fullPrompt')
            .map(([colIdx]) => Number(colIdx));
        if (fullPromptCols.length === 0) return errors;

        // Single mode: only first 3 rows used (set1, set2, set3)
        rows.slice(0, 3).forEach((row, rowIdx) => {
            const setNum = rowIdx + 1;
            const isSet1 = setNum === 1;
            for (const colIdx of fullPromptCols) {
                const cell = (row[colIdx] || '').trim();
                if (!cell) {
                    if (isSet1) errors.push({ rowIdx, colIdx, setNum, msg: `Row ${rowIdx + 1} (Set ${setNum}) Full Prompt cell kosong` });
                    continue;
                }
                const parsed = parseFullPrompt(cell);
                if (parsed.__noMarkers) {
                    errors.push({ rowIdx, colIdx, setNum, msg: `Row ${rowIdx + 1} (Set ${setNum}) Full Prompt tiada markers (perlu [IMAGE], [${requiredMarkers.slice(1).join('], [')}])` });
                    continue;
                }
                if (isSet1) {
                    const missing = requiredMarkers.filter(m => !parsed[m]);
                    if (missing.length > 0) {
                        errors.push({ rowIdx, colIdx, setNum, msg: `Row ${rowIdx + 1} (Set ${setNum}) Full Prompt missing markers: [${missing.join('], [')}]` });
                    }
                }
            }
        });
        return errors;
    }

    // Bulk mode (existing logic)
    const fullPromptMappings = Object.entries(mapping)
        .filter(([, v]) => v && v.endsWith('.fullPrompt'))
        .map(([colIdx, fieldPath]) => ({
            colIdx: Number(colIdx),
            setNum: Number(fieldPath.match(/^set(\d)/)?.[1] || 1),
            fieldPath,
        }));
    if (fullPromptMappings.length === 0) return errors;

    rows.forEach((row, rowIdx) => {
        for (const fp of fullPromptMappings) {
            const cell = (row[fp.colIdx] || '').trim();
            const isSet1 = fp.setNum === 1;
            if (!cell) {
                if (isSet1) errors.push({ rowIdx, colIdx: fp.colIdx, setNum: fp.setNum, msg: `Set ${fp.setNum} Full Prompt cell kosong` });
                continue;
            }
            const parsed = parseFullPrompt(cell);
            if (parsed.__noMarkers) {
                errors.push({ rowIdx, colIdx: fp.colIdx, setNum: fp.setNum, msg: `Set ${fp.setNum} Full Prompt tiada markers (perlu [IMAGE], [${requiredMarkers.slice(1).join('], [')}])` });
                continue;
            }
            if (isSet1) {
                const missing = requiredMarkers.filter(m => !parsed[m]);
                if (missing.length > 0) {
                    errors.push({ rowIdx, colIdx: fp.colIdx, setNum: fp.setNum, msg: `Set ${fp.setNum} Full Prompt missing markers: [${missing.join('], [')}]` });
                }
            }
        }
    });
    return errors;
}

function autoDetectMapping(headers, sceneCount, mode = 'bulk') {
    const mapping = {};
    const lowered = headers.map(h => (h || '').toLowerCase().trim());
    const usedFields = new Set();

    headers.forEach((_, i) => {
        const header = lowered[i];
        if (!header) { mapping[i] = 'skip'; return; }

        if (mode === 'single') {
            // Single mode: optional product number + field type matching (no name/imageUrl, no set prefix)
            // Try productNumber first (no., index, etc.)
            if (!usedFields.has('productNumber') && PRODUCT_KEYWORDS.productNumber.some(k => header === k || header.includes(k))) {
                mapping[i] = 'productNumber';
                usedFields.add('productNumber');
                return;
            }
            // Try fullPrompt (kalau header ada "full prompt" / "prompt full")
            if (!usedFields.has('fullPrompt') && (header.includes('full prompt') || header.includes('prompt full'))) {
                mapping[i] = 'fullPrompt';
                usedFields.add('fullPrompt');
                return;
            }
            for (const [fieldType, keywords] of Object.entries(FIELD_TYPE_KEYWORDS)) {
                if (sceneCount === 2 && fieldType === 'promptVideo') continue;
                if (sceneCount === 1 && (fieldType === 'promptVideo1' || fieldType === 'promptVideo2')) continue;
                if (usedFields.has(fieldType)) continue;
                if (keywords.some(k => header.includes(k))) {
                    mapping[i] = fieldType;
                    usedFields.add(fieldType);
                    return;
                }
            }
            mapping[i] = 'skip';
            return;
        }

        // Bulk mode (existing)
        // 1) Try product-level fields first (name, imageUrl)
        for (const [field, keywords] of Object.entries(PRODUCT_KEYWORDS)) {
            if (usedFields.has(field)) continue;
            if (keywords.some(k => header.includes(k))) {
                mapping[i] = field;
                usedFields.add(field);
                return;
            }
        }

        // 2) Detect set prefix; default to Set 1 if none detected (saves user clicks)
        const setNum = detectSetPrefix(header) || 1;

        // 3) Match field type
        for (const [fieldType, keywords] of Object.entries(FIELD_TYPE_KEYWORDS)) {
            if (sceneCount === 2 && fieldType === 'promptVideo') continue;
            if (sceneCount === 1 && (fieldType === 'promptVideo1' || fieldType === 'promptVideo2')) continue;

            const fullField = `set${setNum}.${fieldType}`;
            if (usedFields.has(fullField)) continue;

            if (keywords.some(k => header.includes(k))) {
                mapping[i] = fullField;
                usedFields.add(fullField);
                return;
            }
        }

        mapping[i] = 'skip';
    });
    return mapping;
}

// Parse TSV/CSV. Default: TSV (Excel/Sheets paste). Falls back to CSV if no tabs.
function parseSpreadsheetData(text) {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length === 0) return { headers: [], rows: [] };
    const useTsv = lines[0].includes('\t');
    const parseLine = (l) => useTsv ? l.split('\t') : l.split(',').map(c => c.trim());
    const allRows = lines.map(parseLine);
    return {
        headers: allRows[0] || [],
        rows: allRows.slice(1).filter(r => r.some(c => (c || '').trim() !== '')),
    };
}

// Step keys per mode (untuk dynamic step navigation + indicator)
const STEPS_BULK = ['scene', 'paste', 'mapping', 'preview'];
const STEPS_SINGLE = ['scene', 'setup', 'paste', 'mapping', 'preview'];
const STEP_LABELS = { scene: 'Scene & Mode', setup: 'Setup Product', paste: 'Paste Data', mapping: 'Mapping', preview: 'Preview' };

export default function BulkImportModal({ isOpen, onClose, onImport, activeModuleId }) {
    const [mode, setMode] = useState('bulk'); // 'bulk' | 'single'
    const [step, setStep] = useState(1); // 1-based index dalam steps array
    // Default 2 — sebab 1-scene currently locked (user fokus 2-scene)
    const [sceneCount, setSceneCount] = useState(2);
    const [rawData, setRawData] = useState('');
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);
    const [mapping, setMapping] = useState({});
    const [parseError, setParseError] = useState('');
    const [uploadedFileName, setUploadedFileName] = useState('');
    const [isParsingFile, setIsParsingFile] = useState(false);
    const fileInputRef = useRef(null);

    // Single-mode product setup state
    const [productSetup, setProductSetup] = useState({ name: '', link: '', imageUrl: '' });
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const imageInputRef = useRef(null);

    const steps = useMemo(() => mode === 'single' ? STEPS_SINGLE : STEPS_BULK, [mode]);
    const currentStepKey = steps[step - 1];

    const fieldOptions = useMemo(() => fieldOptionsForScene(sceneCount, mode), [sceneCount, mode]);
    const requiredFields = useMemo(() => requiredFieldsForScene(sceneCount, mode), [sceneCount, mode]);

    // Validation — which required fields are still unmapped
    // Bulk: setX.fullPrompt mapping satisfies ALL setX.* required fields
    // Single: fullPrompt mapping satisfies all required fields (sebab row IS the set)
    const mappedFieldSet = useMemo(() => new Set(Object.values(mapping).filter(v => v && v !== 'skip')), [mapping]);
    const missingRequired = useMemo(() => requiredFields.filter(f => {
        if (mappedFieldSet.has(f)) return false;
        if (mode === 'single') {
            // Single mode: 'fullPrompt' satisfies all required fields
            if (mappedFieldSet.has('fullPrompt')) return false;
        } else {
            // Bulk mode: setX.fullPrompt covers setX.* required fields
            if (f.startsWith('set')) {
                const setKey = f.split('.')[0];
                if (mappedFieldSet.has(`${setKey}.fullPrompt`)) return false;
            }
        }
        return true;
    }), [requiredFields, mappedFieldSet, mode]);
    const isMappingValid = missingRequired.length === 0;

    // Row-level validation — errors dari fullPrompt cells yang tak comply marker format
    const validationErrors = useMemo(
        () => validateFullPromptRows(rows, mapping, sceneCount, mode),
        [rows, mapping, sceneCount, mode]
    );
    const errorsByRow = useMemo(() => {
        const map = new Map();
        for (const err of validationErrors) {
            if (!map.has(err.rowIdx)) map.set(err.rowIdx, []);
            map.get(err.rowIdx).push(err);
        }
        return map;
    }, [validationErrors]);
    const hasValidationErrors = validationErrors.length > 0;

    // Helper untuk display label tanpa asterisk
    const labelForField = (field) => fieldOptions.find(o => o.value === field)?.label.replace(/ \*$/, '') || field;

    if (!isOpen) return null;

    const reset = () => {
        setMode('bulk');
        setStep(1);
        setSceneCount(2); // Default 2 — 1-scene locked
        setRawData('');
        setHeaders([]);
        setRows([]);
        setMapping({});
        setParseError('');
        setUploadedFileName('');
        setIsParsingFile(false);
        setProductSetup({ name: '', link: '', imageUrl: '' });
        setIsUploadingImage(false);
        setUploadError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (imageInputRef.current) imageInputRef.current.value = '';
    };

    // Upload product image to Firebase Storage (Single mode)
    const handleImageUpload = async (file) => {
        if (!file) return;
        setUploadError('');
        // Size guard (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setUploadError('Image terlalu besar — max 5MB.');
            return;
        }
        if (!file.type.startsWith('image/')) {
            setUploadError('File mesti image (JPG, PNG, WEBP).');
            return;
        }
        setIsUploadingImage(true);
        try {
            const uid = auth.currentUser?.uid;
            if (!uid) throw new Error('Sila log masuk dahulu.');
            const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
            const path = `users/${uid}/smartcustomImages/${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${ext}`;
            const storageRef = fbRef(storage, path);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            setProductSetup(prev => ({ ...prev, imageUrl: url }));
        } catch (err) {
            console.error('[BulkImport] Image upload failed:', err);
            setUploadError(`Upload failed: ${err.message || err}`);
        } finally {
            setIsUploadingImage(false);
        }
    };

    const handleFileUpload = async (file) => {
        if (!file) return;
        setParseError('');
        setUploadedFileName(file.name);
        setIsParsingFile(true);
        try {
            const ext = (file.name.split('.').pop() || '').toLowerCase();
            if (ext === 'csv' || ext === 'tsv' || ext === 'txt') {
                const text = await file.text();
                setRawData(text);
            } else if (ext === 'xlsx' || ext === 'xls') {
                // Dynamic import — xlsx lib (~400KB) hanya load bila user upload Excel
                const XLSX = await import('xlsx');
                const buf = await file.arrayBuffer();
                const wb = XLSX.read(buf, { type: 'array' });
                const firstSheetName = wb.SheetNames[0];
                if (!firstSheetName) {
                    setParseError('Excel file kosong (tiada sheet).');
                    return;
                }
                const ws = wb.Sheets[firstSheetName];
                // Convert ke TSV (FS=\t) supaya parser sedia ada handle
                const tsv = XLSX.utils.sheet_to_csv(ws, { FS: '\t' });
                setRawData(tsv);
            } else {
                setParseError(`Format ".${ext}" tak disokong. Sila guna .xlsx, .xls, .csv, atau .tsv.`);
            }
        } catch (err) {
            console.error('[BulkImport] File parse error:', err);
            setParseError(`Gagal baca file: ${err.message || err}`);
        } finally {
            setIsParsingFile(false);
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleParse = () => {
        setParseError('');
        const { headers: h, rows: r } = parseSpreadsheetData(rawData);
        if (h.length === 0 || r.length === 0) {
            setParseError('Data kosong atau tak boleh parse. Pastikan baris pertama adalah header.');
            return;
        }
        // Single mode requires at least 1 row (3 sets ideally — but allow flexibility)
        if (mode === 'single' && r.length < 1) {
            setParseError('Single Product mode perlu sekurang-kurangnya 1 row (Set 1).');
            return;
        }
        setHeaders(h);
        setRows(r);
        setMapping(autoDetectMapping(h, sceneCount, mode));
        // Advance to next step (Mapping)
        setStep(s => s + 1);
    };

    const handleImport = () => {
        // ─── Single mode: 1 product, rows = sets (max 3 used) ───
        if (mode === 'single') {
            const prompts = getEmptyPromptCollection();
            const setsWithContent = new Set();
            // Auto-hide caption section pada product card kalau caption tak dimap
            // (fullPrompt mapping pun count — sebab markers boleh include [CAPTION])
            const captionMapped = Object.values(mapping).some(v => v === 'captionTiktok' || v === 'fullPrompt');
            // Resolve productNumber from row 1 if mapped
            let productNumber = null;
            const productNumberCol = Object.entries(mapping).find(([, v]) => v === 'productNumber')?.[0];
            if (productNumberCol !== undefined && rows[0]) {
                const raw = (rows[0][Number(productNumberCol)] || '').trim();
                const parsed = parseInt(raw, 10);
                if (!Number.isNaN(parsed)) productNumber = parsed;
            }
            rows.slice(0, 3).forEach((row, setIdx) => {
                const setKey = `set${setIdx + 1}`;
                for (const [colIdx, fieldPath] of Object.entries(mapping)) {
                    if (fieldPath === 'skip' || fieldPath === 'productNumber') continue;
                    const value = (row[Number(colIdx)] || '').trim();
                    if (fieldPath === 'fullPrompt') {
                        if (!value) continue;
                        const parsed = parseFullPrompt(value);
                        if (parsed.__noMarkers) continue;
                        if (parsed.IMAGE) prompts[setKey].promptImage = parsed.IMAGE;
                        if (sceneCount === 2) {
                            if (parsed.SCENE1) prompts[setKey].promptVideo1 = parsed.SCENE1;
                            if (parsed.SCENE2) prompts[setKey].promptVideo2 = parsed.SCENE2;
                        } else {
                            if (parsed.VIDEO) prompts[setKey].promptVideo = parsed.VIDEO;
                        }
                        if (parsed.CAPTION) prompts[setKey].captionTiktok = parsed.CAPTION;
                        setsWithContent.add(setKey);
                    } else {
                        // Direct field name (no set prefix in single mode) — write directly
                        // Field names dah match storage canonical (promptVideo1/promptVideo2)
                        if (prompts[setKey]) {
                            prompts[setKey][fieldPath] = value;
                            if (value) setsWithContent.add(setKey);
                        }
                    }
                }
            });
            const selectedSets = ['set1', ...['set2', 'set3'].filter(s => setsWithContent.has(s))];
            const product = {
                id: `prod_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
                name: productSetup.name || 'Untitled Product',
                link: productSetup.link || '',
                imageUrl: productSetup.imageUrl || null,
                imageBase64: null,
                prompts,
                selectedSets,
                moduleId: 'smartcustom',
                smartcustomSceneCount: sceneCount,
                tab: 'flow',
                status: 'generated',
                logs: [],
                selected: false,
                hideCaption: !captionMapped,
                ...(productNumber !== null && { originalIndex: productNumber }),
            };
            onImport([product]);
            reset();
            return;
        }

        // ─── Bulk mode: caption tracker ───
        // Caption hidden kalau tiada column mapped to *.captionTiktok atau *.fullPrompt
        const bulkCaptionMapped = Object.values(mapping).some(v =>
            v && (v.endsWith('.captionTiktok') || v.endsWith('.fullPrompt'))
        );

        // ─── Bulk mode: rows = products ───
        // Build products from rows + mapping (supports per-set field paths e.g. 'set2.promptImage')
        const newProducts = rows.map((row, idx) => {
            // Empty per-set data; only filled when a column maps to that set+field.
            const prompts = getEmptyPromptCollection();
            // Track which sets have any content — used for selectedSets array
            const setsWithContent = new Set();
            let productName = '';
            let productImageUrl = '';
            let productNumberValue = null;

            for (const [colIdx, fieldPath] of Object.entries(mapping)) {
                if (fieldPath === 'skip') continue;
                const value = (row[Number(colIdx)] || '').trim();
                if (fieldPath === 'name') {
                    productName = value;
                } else if (fieldPath === 'imageUrl') {
                    productImageUrl = value;
                } else if (fieldPath === 'productNumber') {
                    const parsed = parseInt(value, 10);
                    if (!Number.isNaN(parsed)) productNumberValue = parsed;
                } else if (fieldPath.endsWith('.fullPrompt')) {
                    // Auto-split full prompt cell guna markers
                    const setKey = fieldPath.split('.')[0];
                    if (!prompts[setKey] || !value) continue;
                    const parsed = parseFullPrompt(value);
                    if (parsed.__noMarkers) continue; // shouldn't happen — already validated
                    if (parsed.IMAGE) prompts[setKey].promptImage = parsed.IMAGE;
                    if (sceneCount === 2) {
                        if (parsed.SCENE1) prompts[setKey].promptVideo1 = parsed.SCENE1;
                        if (parsed.SCENE2) prompts[setKey].promptVideo2 = parsed.SCENE2;
                    } else {
                        if (parsed.VIDEO) prompts[setKey].promptVideo = parsed.VIDEO;
                    }
                    if (parsed.CAPTION) prompts[setKey].captionTiktok = parsed.CAPTION;
                    setsWithContent.add(setKey);
                } else if (fieldPath.startsWith('set')) {
                    const [setKey, fieldName] = fieldPath.split('.');
                    if (prompts[setKey]) {
                        prompts[setKey][fieldName] = value;
                        if (value) setsWithContent.add(setKey);
                    }
                }
            }
            // Always include set1 in selectedSets (required); add set2/3 if they have content
            const selectedSets = ['set1', ...['set2', 'set3'].filter(s => setsWithContent.has(s))];

            return {
                id: `prod_${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 7)}`,
                name: productName || `Product ${idx + 1}`,
                link: '',
                imageUrl: productImageUrl || null,
                imageBase64: null,
                prompts,
                selectedSets,
                moduleId: 'smartcustom',
                smartcustomSceneCount: sceneCount,
                tab: 'flow',
                status: 'generated',
                logs: [],
                selected: false,
                hideCaption: !bulkCaptionMapped,
                ...(productNumberValue !== null && { originalIndex: productNumberValue }),
            };
        });
        onImport(newProducts);
        reset();
    };

    return (
        <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
            <div className="w-full max-w-4xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-cyan-200 dark:border-cyan-900/50 shadow-cyan-500/10 overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 relative flex-shrink-0">
                    <button
                        onClick={handleClose}
                        className="absolute top-3 right-3 w-8 h-8 bg-white/20 hover:bg-white/30 text-white rounded-full flex items-center justify-center backdrop-blur transition-colors"
                        aria-label="Tutup"
                    >
                        <span className="material-symbols-outlined text-base">close</span>
                    </button>
                    <div className="flex items-center gap-3 pr-10">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur">
                            <span className="material-symbols-outlined text-white text-xl">cloud_upload</span>
                        </div>
                        <div>
                            <h3 className="text-white font-bold text-sm">Bulk Import — SMART</h3>
                            <p className="text-white/80 text-[11px]">Import dari Google Sheet / Excel</p>
                        </div>
                    </div>
                </div>

                {/* Step indicator — dinamik ikut mode */}
                <div className="px-6 pt-3 pb-3 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        {steps.map((key, idx) => {
                            const n = idx + 1;
                            return (
                                <React.Fragment key={key}>
                                    <div className={`flex items-center gap-1.5 ${step >= n ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-400'}`}>
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step >= n ? 'bg-cyan-500 text-white' : 'bg-slate-200 dark:bg-slate-700'}`}>
                                            {step > n ? '✓' : n}
                                        </div>
                                        <span className="text-[10px] font-bold uppercase tracking-wide">{STEP_LABELS[key]}</span>
                                    </div>
                                    {idx < steps.length - 1 && <div className={`flex-1 h-px ${step > n ? 'bg-cyan-500' : 'bg-slate-200 dark:bg-slate-700'}`}></div>}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5 overflow-y-auto flex-1">
                    {currentStepKey === 'scene' && (
                        <div className="space-y-5">
                            {/* Mode picker */}
                            <div>
                                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1">Pilih Mode Import</h4>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">Bulk = banyak produk dari Excel. Single = 1 produk dengan banyak set prompts (image upload sekali).</p>
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        { key: 'bulk', icon: 'inventory_2', title: 'Bulk', desc: '1 row = 1 produk', detail: 'Excel ada Name + Image URL + prompts per row' },
                                        { key: 'single', icon: 'edit_note', title: 'Single Product', desc: '1 produk, banyak set', detail: 'Manual setup + image upload, 3 rows = Set 1/2/3' },
                                    ].map(m => {
                                        const isSelected = mode === m.key;
                                        return (
                                            <button
                                                key={m.key}
                                                onClick={() => setMode(m.key)}
                                                className={`px-4 py-3 rounded-2xl border-2 transition-all text-left ${
                                                    isSelected
                                                        ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30'
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`material-symbols-outlined text-lg ${isSelected ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-400'}`}>{m.icon}</span>
                                                    <span className={`text-sm font-bold ${isSelected ? 'text-cyan-700 dark:text-cyan-300' : 'text-slate-600 dark:text-slate-300'}`}>{m.title}</span>
                                                </div>
                                                <div className={`text-[11px] font-semibold mb-0.5 ${isSelected ? 'text-cyan-600/80' : 'text-slate-500'}`}>{m.desc}</div>
                                                <div className={`text-[10px] ${isSelected ? 'text-cyan-600/70' : 'text-slate-400'}`}>{m.detail}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Scene count */}
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
                                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1">Pilih Scene Count</h4>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">Berapa scene per video? Ini menentukan struktur prompt + script automation.</p>
                            </div>
                            <div className="flex gap-3">
                                {[1, 2].map(n => {
                                    const isLocked = n === 1; // 1-scene locked sementara — fokus 2-scene
                                    const isSelected = sceneCount === n;
                                    return (
                                        <button
                                            key={n}
                                            onClick={() => { if (!isLocked) setSceneCount(n); }}
                                            disabled={isLocked}
                                            className={`flex-1 px-6 py-4 rounded-2xl border-2 transition-all relative ${
                                                isLocked
                                                    ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 cursor-not-allowed opacity-60'
                                                    : isSelected
                                                        ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30'
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                                            }`}
                                        >
                                            {isLocked && (
                                                <span className="material-symbols-outlined absolute top-2 right-2 text-slate-400 text-base">lock</span>
                                            )}
                                            <div className={`text-2xl font-bold mb-1 ${isLocked ? 'text-slate-400' : isSelected ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-400'}`}>{n}</div>
                                            <div className={`text-xs font-bold ${isLocked ? 'text-slate-400' : isSelected ? 'text-cyan-700 dark:text-cyan-300' : 'text-slate-500'}`}>
                                                {n === 1 ? 'Single Scene' : 'Two Scenes'}
                                            </div>
                                            <div className={`text-[10px] mt-1 ${isLocked ? 'text-slate-400 italic' : isSelected ? 'text-cyan-600/80 dark:text-cyan-400/80' : 'text-slate-400'}`}>
                                                {isLocked ? 'Coming Soon — Locked' : `Script: ${n === 1 ? 'ai8saat' : 'ai16saat'}`}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[10px] text-slate-400 italic">
                                Note: Single Scene di-lock sementara — fokus development pada 2-scene dahulu.
                            </p>
                        </div>
                    )}

                    {currentStepKey === 'setup' && (
                        <div className="space-y-4">
                            <div>
                                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1">Setup Product Card</h4>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">Image diupload sekali sahaja. Reuse untuk semua sets.</p>
                            </div>

                            {/* Image upload */}
                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-300 mb-1.5">Product Image *</label>
                                <input
                                    ref={imageInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleImageUpload(e.target.files?.[0])}
                                    className="hidden"
                                />
                                {productSetup.imageUrl ? (
                                    <div className="flex items-start gap-3">
                                        <img src={productSetup.imageUrl} alt="Product" className="w-32 h-32 object-cover rounded-xl border border-slate-200 dark:border-slate-700" />
                                        <div className="flex flex-col gap-2">
                                            <button
                                                onClick={() => imageInputRef.current?.click()}
                                                disabled={isUploadingImage}
                                                className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-all flex items-center gap-1.5"
                                            >
                                                <span className="material-symbols-outlined text-sm">refresh</span>
                                                Tukar Image
                                            </button>
                                            <button
                                                onClick={() => setProductSetup(prev => ({ ...prev, imageUrl: '' }))}
                                                className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-rose-50 dark:bg-rose-950/30 hover:bg-rose-100 text-rose-600 transition-all flex items-center gap-1.5"
                                            >
                                                <span className="material-symbols-outlined text-sm">delete</span>
                                                Buang
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => imageInputRef.current?.click()}
                                        disabled={isUploadingImage}
                                        className="w-full px-4 py-8 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-cyan-500 hover:bg-cyan-50/50 dark:hover:bg-cyan-950/20 transition-all flex flex-col items-center gap-2"
                                    >
                                        <span className="material-symbols-outlined text-3xl text-slate-400">
                                            {isUploadingImage ? 'progress_activity' : 'cloud_upload'}
                                        </span>
                                        <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
                                            {isUploadingImage ? 'Uploading...' : 'Click to upload image'}
                                        </span>
                                        <span className="text-[10px] text-slate-400">JPG, PNG, WEBP — max 5MB</span>
                                    </button>
                                )}
                                {uploadError && (
                                    <div className="mt-2 px-3 py-2 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 rounded-lg text-[11px] text-rose-700 dark:text-rose-300">
                                        {uploadError}
                                    </div>
                                )}
                            </div>

                            {/* Name */}
                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-300 mb-1.5">Product Name *</label>
                                <input
                                    type="text"
                                    value={productSetup.name}
                                    onChange={(e) => setProductSetup(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="cth: Serum Anti-Aging Premium"
                                    className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                            </div>

                            {/* Link */}
                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-300 mb-1.5">Link Product Page <span className="text-slate-400 font-normal">(optional)</span></label>
                                <input
                                    type="url"
                                    value={productSetup.link}
                                    onChange={(e) => setProductSetup(prev => ({ ...prev, link: e.target.value }))}
                                    placeholder="https://shop.tiktok.com/view/product/..."
                                    className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                            </div>
                        </div>
                    )}

                    {currentStepKey === 'paste' && (
                        <div className="space-y-3">
                            <div>
                                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1">Upload File ATAU Paste Data</h4>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                    Pilih file Excel (.xlsx/.xls/.csv) ATAU copy-paste dari Google Sheet / Excel ke textarea di bawah. Termasuk header row.
                                </p>
                            </div>

                            {/* File Upload Button */}
                            <div className="flex items-center gap-3">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls,.csv,.tsv,.txt"
                                    onChange={(e) => handleFileUpload(e.target.files?.[0])}
                                    className="hidden"
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isParsingFile}
                                    className="px-4 py-2 rounded-xl text-xs font-bold bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white shadow-lg shadow-cyan-500/20 transition-all flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-base">
                                        {isParsingFile ? 'progress_activity' : 'upload_file'}
                                    </span>
                                    {isParsingFile ? 'Membaca file...' : 'Upload Excel / CSV'}
                                </button>
                                {uploadedFileName && !isParsingFile && (
                                    <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400">
                                        <span className="material-symbols-outlined text-sm">check_circle</span>
                                        <span className="font-semibold truncate max-w-[300px]" title={uploadedFileName}>{uploadedFileName}</span>
                                    </div>
                                )}
                            </div>

                            {/* OR divider */}
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
                                <span className="text-[10px] font-bold uppercase text-slate-400">atau paste manual</span>
                                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
                            </div>

                            <textarea
                                value={rawData}
                                onChange={(e) => setRawData(e.target.value)}
                                placeholder="Product Name	Image URL	Image Prompt	Video Prompt	Caption TikTok&#10;Serum A	https://...	A serum bottle...	Close-up of serum...	Try this serum! #fyp&#10;Serum B	https://...	..."
                                rows={8}
                                className="w-full px-3 py-2 text-xs font-mono bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-y"
                            />
                            {parseError && (
                                <div className="px-3 py-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-lg text-[11px] text-red-700 dark:text-red-300">
                                    {parseError}
                                </div>
                            )}
                        </div>
                    )}

                    {currentStepKey === 'mapping' && (
                        <div className="space-y-3">
                            <div>
                                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1">
                                    {mode === 'single' ? 'Map Columns to Set Fields' : 'Confirm Column Mapping'}
                                </h4>
                                {mode === 'single' && (
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 italic mb-1">
                                        Single mode: Row 1 = Set 1, Row 2 = Set 2, Row 3 = Set 3. Mapping pilih FIELD dalam set sahaja.
                                    </p>
                                )}
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                    Auto-detect dah cuba match column awak ke field produk. Adjust kalau salah. Field bertanda <span className="text-rose-500 font-bold">*</span> WAJIB diisi.
                                </p>
                            </div>

                            {/* Required fields status indicator */}
                            <div className={`px-3 py-2 rounded-lg border text-[11px] ${
                                isMappingValid
                                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-300'
                                    : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50 text-amber-700 dark:text-amber-300'
                            }`}>
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-base">
                                        {isMappingValid ? 'check_circle' : 'warning'}
                                    </span>
                                    <span className="font-bold">
                                        {isMappingValid
                                            ? `Semua ${requiredFields.length} required fields telah dimap ✓`
                                            : `${requiredFields.length - missingRequired.length} dari ${requiredFields.length} required fields dimap`}
                                    </span>
                                </div>
                                {!isMappingValid && (
                                    <div className="mt-1.5 ml-7 text-[10px]">
                                        Belum dimap: <span className="font-semibold">{missingRequired.map(labelForField).join(', ')}</span>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                {headers.map((h, i) => (
                                    <div key={i} className="flex items-center gap-3 px-3 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] font-bold uppercase text-slate-400">Column {i + 1}</div>
                                            <div className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{h || '(no header)'}</div>
                                            {rows[0] && (
                                                <div className="text-[10px] text-slate-400 truncate mt-0.5">e.g. {rows[0][i] || '(empty)'}</div>
                                            )}
                                        </div>
                                        <span className="material-symbols-outlined text-slate-400 text-base">arrow_forward</span>
                                        <select
                                            value={mapping[i] || 'skip'}
                                            onChange={(e) => setMapping(prev => ({ ...prev, [i]: e.target.value }))}
                                            className="px-3 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 min-w-[200px]"
                                        >
                                            {(() => {
                                                // Group options by `group` field; preserve order
                                                const groups = [];
                                                const seen = new Map();
                                                for (const opt of fieldOptions) {
                                                    const g = opt.group || '__none__';
                                                    if (!seen.has(g)) {
                                                        seen.set(g, groups.length);
                                                        groups.push({ label: opt.group, items: [] });
                                                    }
                                                    groups[seen.get(g)].items.push(opt);
                                                }
                                                return groups.map((g, gi) =>
                                                    g.label
                                                        ? (
                                                            <optgroup key={gi} label={g.label}>
                                                                {g.items.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                            </optgroup>
                                                        )
                                                        : g.items.map(o => <option key={o.value} value={o.value}>{o.label}</option>)
                                                );
                                            })()}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {currentStepKey === 'preview' && (
                        <div className="space-y-3">
                            <div>
                                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1">
                                    {mode === 'single' ? `Preview — 1 Product (${Math.min(rows.length, 3)} sets)` : `Preview — ${rows.length} Products`}
                                </h4>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                    {mode === 'single'
                                        ? `Confirm sebelum import. Product dicipta dengan moduleId = "smartcustom", scene count = ${sceneCount}, ${Math.min(rows.length, 3)} sets pre-filled.`
                                        : `Confirm data sebelum import. Smua products akan dicipta dengan moduleId = "smartcustom" + scene count = ${sceneCount}.`}
                                </p>
                            </div>

                            {/* Single mode: Product card preview */}
                            {mode === 'single' && (
                                <div className="px-4 py-3 bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-200 dark:border-cyan-900/50 rounded-xl">
                                    <div className="flex items-start gap-3">
                                        {productSetup.imageUrl ? (
                                            <img src={productSetup.imageUrl} alt="" className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />
                                        ) : (
                                            <div className="w-20 h-20 bg-slate-200 dark:bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <span className="material-symbols-outlined text-slate-400">image</span>
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold text-slate-700 dark:text-slate-200 truncate">{productSetup.name || '(No name)'}</div>
                                            {productSetup.link && (
                                                <div className="text-[10px] text-cyan-600 dark:text-cyan-400 truncate mt-0.5">{productSetup.link}</div>
                                            )}
                                            <div className="flex gap-2 mt-2">
                                                {[1, 2, 3].map(n => {
                                                    const hasSet = n <= rows.length;
                                                    return (
                                                        <div key={n} className={`px-2 py-0.5 rounded text-[10px] font-bold ${hasSet ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                                                            {hasSet ? '✓' : '○'} Set {n}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Validation Error Banner */}
                            {hasValidationErrors && (
                                <div className="px-3 py-2.5 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 rounded-lg">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <span className="material-symbols-outlined text-rose-600 dark:text-rose-400 text-base">error</span>
                                        <span className="text-[11px] font-bold text-rose-700 dark:text-rose-300">
                                            {validationErrors.length} error dalam {errorsByRow.size} rows — Import disabled
                                        </span>
                                    </div>
                                    <p className="text-[10px] text-rose-600/80 dark:text-rose-400/80 ml-7 mb-2">
                                        Full Prompt cell mesti ada markers [IMAGE], [SCENE1], [SCENE2] (untuk 2-scene) atau [IMAGE], [VIDEO] (1-scene). [CAPTION] optional. Fix spreadsheet, kemudian re-upload.
                                    </p>
                                    <div className="ml-7 max-h-[120px] overflow-y-auto">
                                        <ul className="text-[10px] text-rose-700 dark:text-rose-300 space-y-0.5">
                                            {validationErrors.slice(0, 10).map((e, idx) => (
                                                <li key={idx}>
                                                    <span className="font-bold">Row {e.rowIdx + 1}:</span> {e.msg}
                                                </li>
                                            ))}
                                            {validationErrors.length > 10 && (
                                                <li className="italic opacity-70">...dan {validationErrors.length - 10} error lagi</li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            )}
                            <div className="border border-slate-200 dark:border-slate-700 rounded-lg max-h-[400px] overflow-auto">
                                <table className="text-[11px] border-collapse" style={{ minWidth: 'max-content' }}>
                                    <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-2 py-1.5 text-left font-bold text-slate-400 dark:text-slate-500 uppercase text-[9px] sticky left-0 bg-slate-50 dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700">#</th>
                                            {Object.entries(mapping)
                                                .filter(([, f]) => f !== 'skip')
                                                .map(([i, f]) => {
                                                    const opt = fieldOptions.find(o => o.value === f);
                                                    const display = (opt?.label || f).replace(/ \*$/, '');
                                                    return <th key={i} className="px-3 py-1.5 text-left font-bold text-slate-500 dark:text-slate-400 uppercase text-[9px] whitespace-nowrap" style={{ minWidth: 180 }}>{display}</th>;
                                                })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.slice(0, mode === 'single' ? 3 : 10).map((row, ri) => (
                                            <tr key={ri} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                                <td className="px-2 py-1.5 text-slate-400 font-mono text-[10px] sticky left-0 bg-white dark:bg-slate-900 border-r border-slate-100 dark:border-slate-800">
                                                    {mode === 'single' ? `Set ${ri + 1}` : ri + 1}
                                                </td>
                                                {Object.entries(mapping)
                                                    .filter(([, f]) => f !== 'skip')
                                                    .map(([i]) => (
                                                        <td key={i} className="px-3 py-1.5 text-slate-600 dark:text-slate-300 align-top" style={{ minWidth: 180, maxWidth: 320 }}>
                                                            <div className="truncate" title={row[Number(i)]}>{row[Number(i)] || ''}</div>
                                                        </td>
                                                    ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-[10px] text-slate-400 italic flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-[12px]">swipe</span>
                                Scroll horizontal untuk tengok semua columns. Hover row untuk highlight.
                            </p>
                            {mode === 'bulk' && rows.length > 10 && (
                                <p className="text-[10px] text-slate-400 italic">Showing first 10 of {rows.length} rows. Semua akan diimport.</p>
                            )}
                            {mode === 'single' && rows.length > 3 && (
                                <p className="text-[10px] text-amber-600 italic">⚠ Excel ada {rows.length} rows tapi Single mode hanya guna 3 rows pertama (Set 1, 2, 3). Sisa diabaikan.</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center flex-shrink-0">
                    <button
                        onClick={step === 1 ? handleClose : () => setStep(step - 1)}
                        className="px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-wide bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-all"
                    >
                        {step === 1 ? 'Batal' : '← Back'}
                    </button>

                    {currentStepKey === 'scene' && (
                        <button onClick={() => setStep(s => s + 1)} className="px-5 py-2 rounded-xl font-bold text-xs uppercase tracking-wide bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg shadow-cyan-500/20 transition-all">
                            Next →
                        </button>
                    )}
                    {currentStepKey === 'setup' && (() => {
                        const setupValid = !!productSetup.name.trim() && !!productSetup.imageUrl;
                        return (
                            <button
                                onClick={() => setStep(s => s + 1)}
                                disabled={!setupValid}
                                title={!setupValid ? 'Sila isi Name + upload Image' : ''}
                                className="px-5 py-2 rounded-xl font-bold text-xs uppercase tracking-wide bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 text-white shadow-lg shadow-cyan-500/20 transition-all"
                            >
                                Next →
                            </button>
                        );
                    })()}
                    {currentStepKey === 'paste' && (
                        <button onClick={handleParse} disabled={!rawData.trim()} className="px-5 py-2 rounded-xl font-bold text-xs uppercase tracking-wide bg-cyan-500 hover:bg-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed text-white shadow-lg shadow-cyan-500/20 transition-all">
                            Parse Data →
                        </button>
                    )}
                    {currentStepKey === 'mapping' && (
                        <button
                            onClick={() => setStep(s => s + 1)}
                            disabled={!isMappingValid}
                            title={!isMappingValid ? `Map dahulu: ${missingRequired.map(labelForField).join(', ')}` : ''}
                            className="px-5 py-2 rounded-xl font-bold text-xs uppercase tracking-wide bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 text-white shadow-lg shadow-cyan-500/20 transition-all"
                        >
                            Preview →
                        </button>
                    )}
                    {currentStepKey === 'preview' && (
                        <button
                            onClick={handleImport}
                            disabled={hasValidationErrors}
                            title={hasValidationErrors ? `Fix ${validationErrors.length} errors dahulu` : ''}
                            className="px-5 py-2 rounded-xl font-bold text-xs uppercase tracking-wide bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 text-white shadow-lg shadow-emerald-500/20 transition-all"
                        >
                            <span className="flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-sm">cloud_upload</span>
                                {mode === 'single' ? 'Import 1 Product' : `Import ${rows.length} Products`}
                            </span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
